import { Injectable } from '@angular/core';
import { DynamicTableColDef } from '@frk/eds-components';
import { PortfolioHolding } from '@models';
import { hasValues } from '@products/utils/mappers/mapper.utils';
import { TranslateService } from '@shared/translate/translate.service';
import { FundId } from '@types';
import { Logger } from '@utils/logger';

const logger = Logger.getLogger('PortfolioHoldingsService');

@Injectable({
  providedIn: 'root',
})
export class PortfolioHoldingsService {
  constructor(private translateService: TranslateService) {}

  // removes columns where ALL values are empty
  public removeEmptyCols = (
    colDefs: DynamicTableColDef<PortfolioHolding>[],
    rows: PortfolioHolding[]
  ): DynamicTableColDef<PortfolioHolding>[] =>
    colDefs.filter((colDef: DynamicTableColDef<PortfolioHolding>): boolean =>
      hasValues(rows, colDef.field as keyof PortfolioHolding)
    );

  // returns all holdings grid column defs
  public getAllCols = (
    fundId: FundId
  ): DynamicTableColDef<PortfolioHolding>[] => [
    // WDE-3659: order updated
    {
      field: 'securityName',
      headerName: this.translateService.instant('pds.SECURITY_NAME'),
      footnotePlacement: `Holdings:SecurityName`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-security-name'
      ),
      sortable: true,
    },
    {
      field: 'sectorName',
      headerName: this.translateService.instant('pds.SECTOR_NAME'),
      footnotePlacement: `Holdings:SectorName`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-sector-name'
      ),
      sortable: true,
    },
    {
      field: 'percentageOfNetAssets',
      headerName: this.translateService.instant('pds.PCT_OF_NET_ASSETS'),
      footnotePlacement: `Holdings:PercentageOfNetAssets`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-percentage-of-net-assets'
      ),
      dataType: 'percent',
      sortable: true,
    },
    {
      field: 'marketValue',
      headerName: this.translateService.instant('products.market-value'),
      footnotePlacement: `Holdings:MarketValue`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-market-value'
      ),
      dataType: 'currency',
      sortable: true,
    },
    {
      field: 'notionalMarketValue',
      headerName: this.translateService.instant('pds.NOTIONAL_MARKET_VALUE'),
      footnotePlacement: `Holdings:NotionalMarketValue`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-notional-market-value'
      ),
      dataType: 'number',
      sortable: true,
    },
    {
      field: 'quantityShrpar',
      headerName: this.translateService.instant('pds.QUANTITY_SHRPAR'),
      footnotePlacement: `Holdings:QuantityShrpar`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-quantity-shrpar'
      ),
      dataType: 'number',
      sortable: true,
    },
    {
      field: 'securityTicker',
      headerName: this.translateService.instant('pds.SECURITY_TICKER', fundId),
      footnotePlacement: `Holdings:SecurityTicker`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-security-ticker'
      ),
      sortable: true,
    },
    {
      field: 'isinSecurityNumber',
      headerName: this.translateService.instant(
        'pds.ISIN_SECURITY_NUMBER',
        fundId
      ),
      footnotePlacement: `Holdings:IsinSecurityNumber`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-isin-security-number'
      ),
      sortable: true,
    },
    {
      field: 'originalCouponRate',
      headerName: this.translateService.instant('pds.ORIGINAL_COUPON_RATE'),
      footnotePlacement: `Holdings:OriginalCouponRate`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-original-coupon-rate'
      ),
      dataType: 'number',
      sortable: true,
    },
    {
      field: 'securityExpirationDate',
      headerName: this.translateService.instant('pds.SECURITY_EXPIRATION_DATE'),
      footnotePlacement: `Holdings:SecurityExpirationDate`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-security-expiration-date'
      ),
      dataType: 'date',
      sortable: true,
    },
    {
      field: 'investmentCategory',
      headerName: this.translateService.instant('pds.INVESTMENT_CATEGORY'),
      footnotePlacement: `Holdings:InvestmentCategory`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-investment-category'
      ),
      sortable: true,
    },
    {
      field: 'assetClassCategory',
      headerName: this.translateService.instant('pds.ASSET_CLASS_CATEGORY'),
      footnotePlacement: `Holdings:AssetClassCategory`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-asset-class-category'
      ),
      sortable: true,
    },
    {
      field: 'marketCurrency',
      headerName: this.translateService.instant('pds.MARKET_CURRENCY'),
      footnotePlacement: `Holdings:MarketCurrency`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-market-currency'
      ),
      sortable: true,
    },

    // Cols below weren't specified in updated WDE-3659 list
    {
      field: 'cusipNumber',
      headerName: this.translateService.instant('pds.CUSIP', fundId),
      footnotePlacement: `Holdings:CusipNumber`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-cusip-number'
      ),
      sortable: true,
    },
    {
      field: 'finalMaturityDate',
      headerName: this.translateService.instant('pds.FINAL_MATURITY_DATE'),
      footnotePlacement: `Holdings:FinalMaturityDate`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-final-maturity-date'
      ),
      sortable: true,
    },
    {
      field: 'contracts',
      headerName: this.translateService.instant('pds.CONTRACTS'),
      footnotePlacement: `Holdings:Contracts`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-contracts'
      ),
      sortable: true,
    },
  ];
}
